import {createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type RefObject} from "react";
import {Outlet, useLocation, useNavigate} from "react-router";

import {PATHS} from "../../routing/paths";
import {Header} from "./Header";
import {Navigation} from "./Navigation";

/**
 * Постоянный каркас вкладок нижней навигации: шапка + таб-бар.
 *
 * Правка Ирины 01.10 («экран вздрагивает при переключении вкладок»). Раньше каждая
 * вкладка (Лента, Сообщения, Куда сходить, Профиль) рисовала СВОЮ шапку и СВОЙ
 * таб-бар внутри собственного h-dvh шелла, а холодный чанк — ещё и третий экземпляр
 * (TabRouteLoader). Переход = полный unmount/mount шапки и таб-бара: DOM-узлы,
 * backdrop-filter-слои и подсветка активной вкладки создавались заново, бейджи
 * (непрочитанные, супер-симпатии) на «Куда сходить»/«Профиле» сбрасывались в 0 и
 * возвращались на ленте. Геометрия при этом не менялась (layout-shift = 0), а мигало
 * именно пересоздание хрома — на WebView это видно как «вздрагивание».
 *
 * Теперь шапка и таб-бар живут в layout-route и переживают переходы; страница
 * вкладки отдаёт каркасу данные и обработчики через useTabChrome и рисует только
 * свою область. Значения бейджей не сбрасываются при смене вкладки: страница, у
 * которой своих данных нет, их не передаёт, и каркас держит последние известные.
 */
export type TabChromeData = {
    unreadNotifications: number;
    unreadChats: number;
    isPremium: boolean;
    superLikesRemaining: number;
    /** Капсула таб-бара спрятана, пока открыт модальный слой (#771); полоса держит высоту. */
    navHidden: boolean;
    notificationsAnchorRef?: RefObject<HTMLDivElement>;
};

export type TabChromeHandlers = {
    onOpenFilters: () => void;
    onOpenNotifications: () => void;
    onOpenSuperLikeShop: () => void;
    onOpenSubscription: () => void;
    onOpenHome: () => void;
    onOpenQR: () => void;
    onOpenEvents: () => void;
    onOpenChats: () => void;
    onOpenProfile: () => void;
};

type TabChromeApi = {
    update: (data: Partial<TabChromeData>, handlers: Partial<TabChromeHandlers>) => void;
    release: () => void;
};

const TabChromeContext = createContext<TabChromeApi | null>(null);

/** Данные и обработчики шапки приложения — те же, что у шапки каркаса. */
export type AppHeaderValue = {
    unreadNotifications: number;
    isPremium: boolean;
    superLikesRemaining: number;
    onOpenFilters: () => void;
    onOpenNotifications: () => void;
    onOpenSuperLikeShop: () => void;
    onOpenSubscription: () => void;
    onOpenHome: () => void;
};

/**
 * Шапка приложения для страниц поверх вкладки (issue #1066: страница разбора
 * показывает ту же шапку — логотип, фильтр, корона, колокольчик). Значение то же,
 * что получает шапка каркаса, поэтому счётчик и точка колокольчика совпадают.
 * Вне каркаса вкладок (QR-скан, «Мои ИИ-разборы» отдельной страницей) — null.
 */
export const AppHeaderContext = createContext<AppHeaderValue | null>(null);

export function useAppHeader(): AppHeaderValue | null {
    return useContext(AppHeaderContext);
}

const INITIAL: TabChromeData = {
    unreadNotifications: 0,
    unreadChats: 0,
    isPremium: false,
    superLikesRemaining: 0,
    navHidden: false,
};

const noop = () => {};

type TabSection = "home" | "whereToGo" | "chats" | "profile";

/** Те же правила, что подсветка вкладки в Navigation.tsx. */
function tabOfPath(pathname: string): TabSection | null {
    if (pathname === PATHS.app) return "home";
    if (pathname.startsWith(PATHS.appChats)) return "chats";
    if (pathname.startsWith(PATHS.whereToGo)) return "whereToGo";
    if (pathname.startsWith(PATHS.appProfile)) return "profile";
    return null;
}

type ViewTransitionHandle = {
    ready: Promise<void>;
    finished: Promise<void>;
    updateCallbackDone: Promise<void>;
    skipTransition: () => void;
};
type DocumentWithViewTransitions = Document & {
    startViewTransition?: (update: () => Promise<void> | void) => ViewTransitionHandle;
};

/** Сколько старый кадр ждёт коммита новой вкладки. Чанки вкладок подгружены заранее
 *  (см. preload ниже), обычно коммит — 1–2 кадра; предел — чтобы медленная сеть на
 *  холодном чанке не держала экран замороженным (ввод во время перехода не идёт). */
const TAB_COMMIT_TIMEOUT_MS = 350;
const TAB_TRANSITION_ATTR = "data-rla-tab-transition";

/**
 * Смена вкладки через View Transitions API: старая вкладка остаётся кадром, новая
 * проявляется поверх (opacity + сдвиг 8px, 220 мс, --rla-ease-out; стили — app.css,
 * «Переход между вкладками»). Раньше смена была жёсткой склейкой в один кадр.
 * Нет API или «Уменьшить движение» — мгновенное переключение, как было.
 */
function useTabTransition() {
    const {pathname} = useLocation();
    const pathnameRef = useRef(pathname);
    pathnameRef.current = pathname;
    const commitWaitersRef = useRef<Array<() => void>>([]);
    const activeRef = useRef<ViewTransitionHandle | null>(null);

    // Коммит новой вкладки: каркас перерисовывается тем же обновлением роутера,
    // что и <Outlet/>, — его layout-эффект и есть момент «новый DOM готов».
    useLayoutEffect(() => {
        const waiters = commitWaitersRef.current;
        commitWaitersRef.current = [];
        waiters.forEach((resolve) => resolve());
    }, [pathname]);

    return useCallback((target: TabSection, run: () => void) => {
        const doc = document as DocumentWithViewTransitions;
        const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
        if (tabOfPath(pathnameRef.current) === target || !doc.startViewTransition || reduceMotion) {
            run();
            return;
        }
        // Тап по следующей вкладке посреди перехода: прежний доигрывать не нужно.
        activeRef.current?.skipTransition();
        const root = document.documentElement;
        root.setAttribute(TAB_TRANSITION_ATTR, "");
        const transition = doc.startViewTransition(() => new Promise<void>((resolve) => {
            const timer = window.setTimeout(resolve, TAB_COMMIT_TIMEOUT_MS);
            commitWaitersRef.current.push(() => {
                window.clearTimeout(timer);
                resolve();
            });
            run();
        }));
        activeRef.current = transition;
        // skipTransition() отклоняет ready — это штатно, не ошибка.
        transition.ready.catch(noop);
        transition.updateCallbackDone.catch(noop);
        transition.finished.catch(noop).finally(() => {
            if (activeRef.current !== transition) return;
            activeRef.current = null;
            root.removeAttribute(TAB_TRANSITION_ATTR);
        });
    }, []);
}

/**
 * Страница вкладки подключает каркас: данные шапки/таб-бара и обработчики.
 * Вызывается на каждом рендере (как обычные пропсы); лишних перерисовок каркаса нет —
 * обработчики лежат в ref, а данные сравниваются поэлементно.
 */
export function useTabChrome(data: Partial<TabChromeData>, handlers: Partial<TabChromeHandlers>) {
    const api = useContext(TabChromeContext);
    useLayoutEffect(() => {
        api?.update(data, handlers);
    });
    useLayoutEffect(() => () => api?.release(), [api]);
}

export function TabShellLayout() {
    const navigate = useNavigate();
    const [data, setData] = useState<TabChromeData>(INITIAL);
    const switchTab = useTabTransition();

    // Код всех четырёх вкладок — заранее, в простое: первый переход на вкладку не
    // упирается в загрузку чанка (роутер в это время держит старую страницу, и
    // переход стартовал бы с запозданием или без кадра новой вкладки).
    useEffect(() => {
        const preload = () => {
            void import("../../pages/app/Swipes");
            void import("../../pages/app/Chats");
            void import("../../pages/app/WhereToGoPage");
            void import("../../pages/app/ProfilePage");
        };
        if (typeof window.requestIdleCallback === "function") {
            const id = window.requestIdleCallback(preload, {timeout: 3000});
            return () => window.cancelIdleCallback(id);
        }
        const id = window.setTimeout(preload, 1500);
        return () => window.clearTimeout(id);
    }, []);

    const defaults = useMemo<TabChromeHandlers>(() => ({
        onOpenFilters: noop,
        onOpenNotifications: noop,
        onOpenSuperLikeShop: noop,
        onOpenSubscription: noop,
        onOpenHome: () => navigate(PATHS.app),
        onOpenQR: noop,
        onOpenEvents: noop,
        onOpenChats: () => navigate(PATHS.appChats),
        onOpenProfile: () => navigate(PATHS.appProfile),
    }), [navigate]);

    const handlersRef = useRef<TabChromeHandlers>(defaults);
    const defaultsRef = useRef(defaults);
    defaultsRef.current = defaults;

    const api = useMemo<TabChromeApi>(() => ({
        update: (next, handlers) => {
            handlersRef.current = {...defaultsRef.current, ...handlers};
            setData((prev) => {
                const merged = {...prev};
                let changed = false;
                for (const key of Object.keys(next) as (keyof TabChromeData)[]) {
                    const value = next[key];
                    if (value !== undefined && merged[key] !== value) {
                        (merged as Record<string, unknown>)[key] = value;
                        changed = true;
                    }
                }
                return changed ? merged : prev;
            });
        },
        // Страница ушла: обработчики умирают вместе с её состоянием, данные остаются.
        release: () => {
            handlersRef.current = defaultsRef.current;
            setData((prev) => (prev.navHidden ? {...prev, navHidden: false} : prev));
        },
    }), []);

    // Стабильные обёртки: Header/Navigation — memo, и перерисовываются только по данным.
    const call = useCallback(<K extends keyof TabChromeHandlers>(key: K) => () => handlersRef.current[key](), []);
    const stable = useMemo(() => ({
        onOpenFilters: call("onOpenFilters"),
        onOpenNotifications: call("onOpenNotifications"),
        onOpenSuperLikeShop: call("onOpenSuperLikeShop"),
        onOpenSubscription: call("onOpenSubscription"),
        onOpenHome: call("onOpenHome"),
        onOpenQR: call("onOpenQR"),
        onOpenEvents: call("onOpenEvents"),
        onOpenChats: call("onOpenChats"),
        onOpenProfile: call("onOpenProfile"),
    }), [call]);

    // Переходы между вкладками — через useTabTransition (и с логотипа «На главную»).
    const tabs = useMemo(() => ({
        onOpenHome: () => switchTab("home", stable.onOpenHome),
        onOpenChats: () => switchTab("chats", stable.onOpenChats),
        onOpenProfile: () => switchTab("profile", stable.onOpenProfile),
        onOpenWhereToGo: () => switchTab("whereToGo", () => navigate(PATHS.whereToGo)),
    }), [switchTab, stable, navigate]);

    const header = useMemo<AppHeaderValue>(() => ({
        unreadNotifications: data.unreadNotifications,
        isPremium: data.isPremium,
        superLikesRemaining: data.superLikesRemaining,
        onOpenFilters: stable.onOpenFilters,
        onOpenNotifications: stable.onOpenNotifications,
        onOpenSuperLikeShop: stable.onOpenSuperLikeShop,
        onOpenSubscription: stable.onOpenSubscription,
        onOpenHome: tabs.onOpenHome,
    }), [data.unreadNotifications, data.isPremium, data.superLikesRemaining, stable, tabs]);

    return (
        <TabChromeContext.Provider value={api}>
        <AppHeaderContext.Provider value={header}>
            <div className="rla-shell rla-tabshell relative size-full h-dvh min-h-dvh flex flex-col overflow-x-hidden">
                <Header
                    unreadNotifications={data.unreadNotifications}
                    isPremium={data.isPremium}
                    superLikesRemaining={data.superLikesRemaining}
                    onOpenFilters={stable.onOpenFilters}
                    onOpenNotifications={stable.onOpenNotifications}
                    onOpenSuperLikeShop={stable.onOpenSuperLikeShop}
                    onOpenSubscription={stable.onOpenSubscription}
                    onOpenHome={tabs.onOpenHome}
                    notificationsAnchorRef={data.notificationsAnchorRef}
                />

                {/* Страница вкладки (или спиннер холодного чанка) — единственное, что меняется.
                    Правка Ирины 05.10 («высота показывается по-разному на разных браузерах,
                    таб-бар оставался посередине экрана»): страница вкладки растягивается на
                    всё пространство между шапкой и полосой навигации независимо от того, как
                    браузер отдал dvh/fallback-высоту шелла — таб-бар всегда прижат к нижней
                    кромке (env(safe-area-inset-bottom) учитывается в .rla-nav отдельно). */}
                <div className="flex min-h-0 flex-1 flex-col">
                    <Outlet/>
                </div>

                {/* #771: при открытом модальном слое капсулу не показываем — иначе она
                    просвечивает в полосе под центрированной мэтч-карточкой. Но и не
                    размонтируем (правка 02.10): полоса держит высоту, <main> не
                    прыгает, капсула уходит и возвращается анимацией (Navigation.tsx). */}
                <Navigation
                    hidden={data.navHidden}
                    unreadChats={data.unreadChats}
                    onOpenQR={stable.onOpenQR}
                    onOpenEvents={stable.onOpenEvents}
                    onOpenHome={tabs.onOpenHome}
                    onOpenChats={tabs.onOpenChats}
                    onOpenProfile={tabs.onOpenProfile}
                    onOpenWhereToGo={tabs.onOpenWhereToGo}
                />
            </div>
        </AppHeaderContext.Provider>
        </TabChromeContext.Provider>
    );
}
